import { useState, useEffect, useCallback, useRef } from 'react';
import { forceHardReload } from '../utils/cacheUtils';
import { logger } from '../utils/logger';
import {
  getCachedVersion,
  getInitialBuildId,
  updateCachedVersion,
  isVersionStateInitialized,
  type VersionInfo,
} from '../utils/versionState';

/**
 * Version Check Hook
 *
 * Provides proactive version checking to detect when a new deployment
 * has occurred. Prevents chunk load errors by detecting version mismatches
 * before they cause issues.
 *
 * Features:
 * - Background check every 5 minutes (reduced from 10 for faster detection)
 * - Route change check with 15-second caching (reduced from 60)
 * - Auto force-refresh on version mismatch during navigation
 * - Manual refresh via notification banner
 * - Syncs with versionState module for consistency with lazyWithReload
 *
 * @returns {Object} Version check state and methods
 * @returns {boolean} hasUpdate - True if newer version available
 * @returns {Function} performUpdate - Manually trigger force-refresh
 * @returns {Function} checkOnRouteChange - Check version on route transitions
 *
 * @example
 * function Layout() {
 *   const { hasUpdate, performUpdate, checkOnRouteChange } = useVersionCheck();
 *
 *   // Check on route changes
 *   const location = useLocation();
 *   useEffect(() => {
 *     checkOnRouteChange();
 *   }, [location, checkOnRouteChange]);
 *
 *   return (
 *     <>
 *       {hasUpdate && <UpdateBanner onRefresh={performUpdate} />}
 *       <Outlet />
 *     </>
 *   );
 * }
 */
export function useVersionCheck() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null);

  // Cache for route change checks (reduced from 60s to 15s for faster detection)
  const versionCacheRef = useRef<{
    version: VersionInfo | null;
    timestamp: number;
  }>({
    version: null,
    timestamp: 0,
  });

  // Tracking flag to prevent multiple simultaneous fetches
  const isFetchingRef = useRef(false);

  /**
   * Fetch version.json from server with cache-busting and retry logic
   * Retries up to 3 times with exponential backoff on network failures
   */
  const fetchVersion = useCallback(async (): Promise<VersionInfo | null> => {
    const MAX_RETRIES = 3;
    const BASE_DELAY = 1000; // 1 second

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        // Cache-busting query parameter
        const cacheBuster = Date.now();
        const response = await fetch(`/static/dist/version.json?_=${cacheBuster}`);

        if (!response.ok) {
          // 404 or server error - don't retry, it's a permanent failure
          if (response.status === 404) {
            console.error('[VersionCheck] version.json not found (404)');
            return null;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const versionInfo: VersionInfo = await response.json();

        // Sync with versionState module
        updateCachedVersion(versionInfo);

        return versionInfo;
      } catch (error) {
        const isLastAttempt = attempt === MAX_RETRIES - 1;

        if (isLastAttempt) {
          // Log to console in production for visibility on critical failures
          console.error('[VersionCheck] Failed to fetch version after retries:', error);
          return null;
        }

        // Exponential backoff: 1s, 2s, 4s
        const delay = BASE_DELAY * Math.pow(2, attempt);
        logger.warn(`Version check attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    return null;
  }, []);

  /**
   * Load initial version from versionState module or localStorage
   */
  const loadInitialVersion = useCallback(async () => {
    // First check if versionState module already has the version
    if (isVersionStateInitialized()) {
      const cachedVersion = getCachedVersion();
      if (cachedVersion) {
        setCurrentVersion(cachedVersion);
        versionCacheRef.current = {
          version: cachedVersion,
          timestamp: Date.now(),
        };
        return;
      }
    }

    // Fallback: Try localStorage
    const storedVersion = localStorage.getItem('app_version');
    if (storedVersion) {
      try {
        const parsed: VersionInfo = JSON.parse(storedVersion);
        setCurrentVersion(parsed);
        versionCacheRef.current = {
          version: parsed,
          timestamp: Date.now(),
        };
      } catch (error) {
        logger.warn('Failed to parse stored version:', error);
      }
    }

    // Fetch current version from server
    const serverVersion = await fetchVersion();
    if (serverVersion) {
      // Store as current version
      localStorage.setItem('app_version', JSON.stringify(serverVersion));
      setCurrentVersion(serverVersion);
      versionCacheRef.current = {
        version: serverVersion,
        timestamp: Date.now(),
      };
    }
  }, [fetchVersion]);

  /**
   * Check if there's a version mismatch
   */
  const checkForUpdate = useCallback(async () => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;

    try {
      const serverVersion = await fetchVersion();

      if (!serverVersion) {
        isFetchingRef.current = false;
        return;
      }

      // Get the initial buildId from versionState (what main.js was built with)
      const initialBuildId = getInitialBuildId();

      // Compare against initial buildId if available, otherwise current version
      const compareBuildId = initialBuildId || currentVersion?.buildId;

      if (compareBuildId && serverVersion.buildId !== compareBuildId) {
        setHasUpdate(true);
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [currentVersion, fetchVersion]);

  /**
   * Check version on route change with smart caching
   *
   * Uses 15-second cache to minimize overhead while detecting updates quickly:
   * - First check in 15s window: ~50ms (fetch)
   * - Subsequent checks: 0ms (cached)
   *
   * If version mismatch detected, automatically force-refreshes the page.
   *
   * NOTE: This is a secondary check. The primary check happens in
   * lazyWithReload (routes.tsx) BEFORE the lazy import starts.
   * This provides an additional layer of detection.
   *
   * @returns {boolean} True if check was performed (not cached)
   */
  const checkOnRouteChange = useCallback(async (): Promise<boolean> => {
    const now = Date.now();
    const CACHE_DURATION = 15000; // 15 seconds (reduced from 60s)

    // Get the initial buildId that main.js was built with
    const initialBuildId = getInitialBuildId();

    // Use cached version if within cache window
    if (versionCacheRef.current.version && now - versionCacheRef.current.timestamp < CACHE_DURATION) {
      // Use cached result - compare with initial buildId
      const cachedVersion = versionCacheRef.current.version;
      const compareBuildId = initialBuildId || currentVersion?.buildId;

      if (compareBuildId && cachedVersion.buildId !== compareBuildId) {
        // Auto force-refresh on route change if version mismatch
        logger.log('[VersionCheck] Cached version mismatch detected, reloading...');
        forceHardReload(false, false);
        return true;
      }
      return false; // Used cache, no new check
    }

    // Cache expired or no cache - fetch new version
    if (isFetchingRef.current) return false;

    isFetchingRef.current = true;

    try {
      const serverVersion = await fetchVersion();

      if (!serverVersion) {
        isFetchingRef.current = false;
        return false;
      }

      // Update cache
      versionCacheRef.current = {
        version: serverVersion,
        timestamp: now,
      };

      // Compare with initial buildId
      const compareBuildId = initialBuildId || currentVersion?.buildId;

      if (compareBuildId && serverVersion.buildId !== compareBuildId) {
        // Auto force-refresh on route change if version mismatch
        logger.log('[VersionCheck] Fresh version mismatch detected, reloading...');
        forceHardReload(false, false);
        return true;
      }

      return true; // Performed fresh check
    } catch (error) {
      logger.warn('Route change version check failed:', error);
      return false;
    } finally {
      isFetchingRef.current = false;
    }
  }, [currentVersion, fetchVersion]);

  /**
   * Manually trigger force-refresh
   * Called when user clicks "Refresh Now" in notification banner
   */
  const performUpdate = useCallback(() => {
    forceHardReload(false, false);
  }, []);

  // Load initial version on mount
  useEffect(() => {
    loadInitialVersion();
  }, [loadInitialVersion]);

  // Set up background check every 5 minutes (reduced from 10)
  useEffect(() => {
    if (!currentVersion) return;

    const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes (was 10)

    const intervalId = setInterval(() => {
      checkForUpdate();
    }, CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [currentVersion, checkForUpdate]);

  return {
    hasUpdate,
    performUpdate,
    checkOnRouteChange,
  };
}
